import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import partnerService from '../services/partnerService';
import BillingTab from './BillingTab';
import accountService from '../services/accountService';
import pricingService from '../services/pricingService';
import productsService from '../services/productsService';
import revShareService from '../services/revShareService';
import { useAuth } from '../contexts/AuthContext';
import CountrySelect from './CountrySelect';
import PartnerSsoSettings from './PartnerSsoSettings';
import PartnerUsers from './PartnerUsers';
import RevShareTierTable from './RevShareTierTable';
import StripeConnectPanel from './StripeConnectPanel';
import './PartnerForm.css';
import CurrencySelect from './CurrencySelect';


const formatPriceLabel = (price) => {
    const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.unit_amount / 100);
    if (!price.recurring) return `${amount} one-time`;
    const { interval, interval_count } = price.recurring;
    return interval_count === 1 ? `${amount} / ${interval}` : `${amount} / ${interval_count} ${interval}s`;
};

const PartnerForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { isSuperAdmin } = useAuth();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  // The Billing tab on the edit page: saved by Update Partner, like every other tab here (BillingTab `mode`).
  const billingRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsCurrentPage, setAccountsCurrentPage] = useState(1);
  const [accountSearch, setAccountSearch] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe_return') || params.get('stripe_refresh')) return 'stripe';
    // Addresses was replaced by Billing (V564); old ?tab=addresses links land on its successor.
    return params.get('tab') === 'addresses' ? 'billing' : (params.get('tab') || 'hotelchains');
  });
  const accountsPerPage = 5;

  // Products tab
  const [platformProducts, setPlatformProducts] = useState([]);
  const [allStripeProducts, setAllStripeProducts] = useState([]);
  const [productConfigs, setProductConfigs] = useState({});
  const [selectedProducts, setSelectedProducts] = useState(new Set()); // product names the partner sells
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsSaving, setProductsSaving] = useState(false);
  const [productsMsg, setProductsMsg] = useState(null);
  const [creatingProductKey, setCreatingProductKey] = useState(null); // { productType, slotKey }
  const [partnerPriceModal, setPartnerPriceModal] = useState(null);   // includes productType
  const [archivingPartnerPriceId, setArchivingPartnerPriceId] = useState(null);

  // Rev share tab
  const [revShareProduct, setRevShareProduct] = useState('vault');
  const [revShareTiers, setRevShareTiers] = useState([]);
  const [revShareLoading, setRevShareLoading] = useState(false);
  const [revShareSaving, setRevShareSaving] = useState(false);
  const [revShareMsg, setRevShareMsg] = useState(null);

  // Switch tab when URL search params change (Stripe redirect or returning from sub-page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe_return') || params.get('stripe_refresh')) {
      setActiveTab('stripe');
    } else if (params.get('tab')) {
      setActiveTab(params.get('tab') === 'addresses' ? 'billing' : params.get('tab'));
    }
  }, [location.search]);
  const [formData, setFormData] = useState({
    companyname: '',
    phone: '',
    mobile: '',
    email: '',
    country: '',
    currency: '',
    confirmednda: 'N',
    confirmedgdpr: 'N',
    vatno: '',
    stripeid: '',
    modconnectors: '',
    modesb: '',
    modsync: '',
    moduniversalapi: '',
    modvault: 'N',
    modoperasync: 'N',
    skip_stripe: 'N',
    invoices_enabled: 0
  });

  useEffect(() => {
    if (id && (isViewMode || isEditMode)) {
      fetchPartner();
      fetchAccounts();
    }
  }, [id, mode]);

  useEffect(() => {
    if (id && activeTab === 'products') {
      loadProducts();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (id && activeTab === 'revshare') {
      loadRevShareTiers(revShareProduct);
    }
  }, [id, activeTab, revShareProduct]);

  const loadRevShareTiers = async (product) => {
    setRevShareLoading(true);
    setRevShareTiers([]);
    try {
      const res = await revShareService.getPartnerTiers(id, product);
      setRevShareTiers(res.data?.tiers || res.tiers || []);
    } catch (err) {
      setRevShareMsg({ type: 'error', text: 'Failed to load revenue share tiers' });
    } finally {
      setRevShareLoading(false);
    }
  };

  const showRevShareMsg = (text, type = 'success') => {
    setRevShareMsg({ text, type });
    setTimeout(() => setRevShareMsg(null), 3000);
  };

  const handleRevShareSave = async (tierId, data) => {
    setRevShareSaving(true);
    try {
      await revShareService.updatePartnerTier(tierId, data);
      showRevShareMsg('Tier updated');
      await loadRevShareTiers(revShareProduct);
    } catch (err) {
      showRevShareMsg('Failed to update tier', 'error');
    } finally {
      setRevShareSaving(false);
    }
  };

  const handleRevShareDelete = async (tierId) => {
    if (!window.confirm('Delete this tier?')) return;
    setRevShareSaving(true);
    try {
      await revShareService.deletePartnerTier(tierId);
      showRevShareMsg('Tier deleted');
      await loadRevShareTiers(revShareProduct);
    } catch (err) {
      showRevShareMsg('Failed to delete tier', 'error');
    } finally {
      setRevShareSaving(false);
    }
  };

  const handleRevShareAdd = async (data) => {
    setRevShareSaving(true);
    try {
      await revShareService.createPartnerTier(id, revShareProduct, data);
      showRevShareMsg('Tier added');
      await loadRevShareTiers(revShareProduct);
    } catch (err) {
      showRevShareMsg('Failed to add tier', 'error');
    } finally {
      setRevShareSaving(false);
    }
  };

  const handleCopyDefault = async () => {
    if (!window.confirm(`This will replace all partner-specific tiers for ${REV_SHARE_PRODUCTS.find(p => p.key === revShareProduct)?.label} with the default tiers. Continue?`)) return;
    setRevShareSaving(true);
    try {
      await revShareService.copyDefaultToPartner(id, revShareProduct);
      showRevShareMsg('Default tiers copied');
      await loadRevShareTiers(revShareProduct);
    } catch (err) {
      showRevShareMsg('Failed to copy default tiers', 'error');
    } finally {
      setRevShareSaving(false);
    }
  };

  const REV_SHARE_PRODUCTS = [
    { key: 'vault',         label: 'Vault' },
    { key: 'paymentporter', label: 'Payment Porter' },
    { key: 'sync',          label: 'Sync' },
  ];

  const PARTNER_SLOT_DEFS = {
    upfront: { ext: `p-${id}-1yr-upfront`,    interval: null,   interval_count: null },
    annual:  { ext: `p-${id}-1yr-reoccuring`, interval: 'year', interval_count: 1 },
    monthly: { ext: `p-${id}-1mo-reoccuring`, interval: 'month', interval_count: 1 },
  };

  const flashProductsMsg = (text, type = 'success') => {
    setProductsMsg({ type, text });
    setTimeout(() => setProductsMsg(null), 4000);
  };

  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsMsg(null);
    try {
      const platformRes = await productsService.list();
      const products = platformRes.data?.products || [];
      setPlatformProducts(products);
      if (!products.length) return;

      const [stripeRes, platformSelectionRes, ...configResults] = await Promise.all([
        pricingService.listProducts(products[0].name),
        pricingService.getPartnerPlatformProducts(id),
        ...products.map(p => pricingService.getConfig(p.name)),
      ]);

      const stripeProducts = stripeRes.data?.products || [];
      setAllStripeProducts(stripeProducts);

      const configs = {};
      products.forEach((p, i) => {
        configs[p.name] = configResults[i].data?.config || { price_prefix: '' };
      });
      setProductConfigs(configs);

      // Build selected set from saved DB selection, falling back to Stripe-derived detection
      const savedIds = new Set((platformSelectionRes.data?.productIds || []).map(String));
      const active = new Set();
      products.forEach((p, i) => {
        if (savedIds.has(String(p.id))) {
          active.add(p.name);
          return;
        }
        // Fall back: detect from Stripe naming convention
        const prefix = configResults[i].data?.config?.price_prefix;
        if (prefix && stripeProducts.some(sp => sp.name.startsWith(`${prefix}p-${id}-`))) {
          active.add(p.name);
        }
      });
      setSelectedProducts(active);
    } catch (err) {
      setProductsMsg({ type: 'error', text: 'Failed to load products' });
    } finally {
      setProductsLoading(false);
    }
  };

  const getSlotProduct = (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    if (!prefix) return null;
    const name = `${prefix}${PARTNER_SLOT_DEFS[slotKey].ext}`;
    return allStripeProducts.find(p => p.name === name) || null;
  };

  const createPartnerSlotProduct = async (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    const name = `${prefix}${PARTNER_SLOT_DEFS[slotKey].ext}`;
    setCreatingProductKey({ productType, slotKey });
    try {
      const res = await pricingService.createProduct(productType, { name });
      setAllStripeProducts(prev => [...prev, res.data.product]);
      flashProductsMsg(`Product "${name}" created in Stripe`);
    } catch (err) {
      flashProductsMsg(err.response?.data?.error?.message || 'Failed to create product', 'error');
    } finally {
      setCreatingProductKey(null);
    }
  };

  const openAddPartnerPrice = (productType, productId, slotKey) => {
    const slot = PARTNER_SLOT_DEFS[slotKey];
    const prefix = productConfigs[productType]?.price_prefix || '';
    setPartnerPriceModal({
      productType,
      productId,
      amount: '',
      currency: 'EUR',
      interval: slot.interval,
      interval_count: slot.interval_count,
      nickname: '',
      lookup_key: `${prefix}${slot.ext}`,
    });
  };

  const savePartnerPrice = async () => {
    setProductsSaving(true);
    try {
      const res = await pricingService.createPrice(partnerPriceModal.productType, {
        productId: partnerPriceModal.productId,
        amount: partnerPriceModal.amount,
        currency: partnerPriceModal.currency,
        ...(partnerPriceModal.interval ? { interval: partnerPriceModal.interval, interval_count: partnerPriceModal.interval_count } : {}),
        nickname: partnerPriceModal.nickname || undefined,
        lookup_key: partnerPriceModal.lookup_key || undefined,
      });
      const newPrice = res.data.price;
      setAllStripeProducts(prev => prev.map(p =>
        p.id === partnerPriceModal.productId
          ? { ...p, prices: [...p.prices, newPrice].sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0)) }
          : p
      ));
      setPartnerPriceModal(null);
      flashProductsMsg('Price created');
    } catch (err) {
      flashProductsMsg(err.response?.data?.error?.message || 'Failed to create price', 'error');
    } finally {
      setProductsSaving(false);
    }
  };

  const archivePartnerPrice = async (productType, productId, priceId) => {
    if (!window.confirm('Archive this price? It will no longer be available.')) return;
    setArchivingPartnerPriceId(priceId);
    try {
      await pricingService.archivePrice(productType, priceId);
      setAllStripeProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, prices: p.prices.filter(pr => pr.id !== priceId) } : p
      ));
      flashProductsMsg('Price archived');
    } catch (err) {
      flashProductsMsg(err.response?.data?.error?.message || 'Failed to archive price', 'error');
    } finally {
      setArchivingPartnerPriceId(null);
    }
  };

  const renderPartnerSlot = (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    const product = getSlotProduct(productType, slotKey);
    const fullName = prefix
      ? `${prefix}${PARTNER_SLOT_DEFS[slotKey].ext}`
      : `{prefix}${PARTNER_SLOT_DEFS[slotKey].ext}`;
    const isCreating = creatingProductKey?.productType === productType && creatingProductKey?.slotKey === slotKey;
    return (
      <div className="partner-product-slot">
        <div className="partner-product-name">{fullName}</div>
        {!product ? (
          <div className="partner-product-missing">
            <span className="partner-product-missing-text">Not yet created in Stripe.</span>
            <button
              type="button"
              className="btn-primary btn-sm-partner"
              onClick={() => createPartnerSlotProduct(productType, slotKey)}
              disabled={!prefix || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create in Stripe'}
            </button>
          </div>
        ) : (
          <div className="partner-prices-section">
            <div className="partner-prices-header">
              <span className="partner-prices-label">Prices</span>
              <button type="button" className="btn-add-partner-price" onClick={() => openAddPartnerPrice(productType, product.id, slotKey)}>
                + Add Price
              </button>
            </div>
            {product.prices.length === 0 ? (
              <div className="partner-no-prices">No prices configured.</div>
            ) : (
              <table className="partner-prices-table">
                <thead>
                  <tr><th>Amount</th><th>Billing</th><th>Nickname</th><th>Lookup Key</th><th></th></tr>
                </thead>
                <tbody>
                  {product.prices.map(price => (
                    <tr key={price.id} className={archivingPartnerPriceId === price.id ? 'partner-price-row-archiving' : ''}>
                      <td className="ppt-amount">{formatPriceLabel(price)}</td>
                      <td className="ppt-interval">{price.recurring ? (price.recurring.interval_count === 1 ? `/ ${price.recurring.interval}` : `/ ${price.recurring.interval_count} ${price.recurring.interval}s`) : 'one-time'}</td>
                      <td>{price.nickname || <span className="ppt-muted">—</span>}</td>
                      <td className="ppt-lookup-key">{price.lookup_key || <span className="ppt-muted">—</span>}</td>
                      <td className="ppt-actions">
                        <button
                          type="button"
                          className="btn-danger-sm"
                          onClick={() => archivePartnerPrice(productType, product.id, price.id)}
                          disabled={archivingPartnerPriceId === price.id}
                        >
                          {archivingPartnerPriceId === price.id ? 'Archiving...' : 'Archive'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderProductOverrideSection = (platformProduct) => {
    const { name, display_name } = platformProduct;
    const prefix = productConfigs[name]?.price_prefix;
    return (
      <div key={name} className="partner-override-section" style={{ marginBottom: '2rem' }}>
        <h4 className="partner-override-title">{display_name} Override Pricing</h4>
        <p className="partner-override-desc">
          Partner-specific prices for {display_name}. These override the default pricing at checkout.
          If not set, the default pricing applies.
        </p>
        {!prefix && (
          <div className="partner-prefix-notice">
            Price prefix not configured for {display_name}. Set it in Admin → Products → {display_name} → Pricing Configuration.
          </div>
        )}
        <div className="partner-override-group">
          <div className="partner-override-group-label">
            Fixed Price Upfront <span className="partner-billing-badge badge-invoice">Invoice / Bank Transfer</span>
          </div>
          <p className="partner-override-group-note">Price × retention years = invoice total.</p>
          {renderPartnerSlot(name, 'upfront')}
        </div>
        <div className="partner-override-group">
          <div className="partner-override-group-label">
            Recurring Billing <span className="partner-billing-badge badge-card">Credit Card</span>
          </div>
          <p className="partner-override-group-note">Creates a Stripe subscription at checkout.</p>
          <div className="partner-recurring-label">Annual</div>
          {renderPartnerSlot(name, 'annual')}
          <div className="partner-recurring-label partner-recurring-label-spaced">Monthly</div>
          {renderPartnerSlot(name, 'monthly')}
        </div>
      </div>
    );
  };


  const fetchPartner = async () => {
    try {
      setLoading(true);
      const response = await partnerService.getPartnerById(id);
      setFormData(response.data.partner);
    } catch (err) {
      setError('Failed to fetch partner');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await accountService.getAllAccounts({ partnerid: id });
      console.log('Accounts response:', response);
      // Handle different response formats - prioritize response.data based on fetchPartner pattern
      const accountsData = response.data?.accounts || response.accounts || (Array.isArray(response.data) ? response.data : []);
      console.log('Accounts data:', accountsData);
      setAccounts(accountsData);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  useEffect(() => {
    setAccountsCurrentPage(1);
  }, [id, accountSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyname) { setError('Company Name is required'); return; }
    if (!formData.email)       { setError('Email is required'); return; }
    if (!formData.country)     { setError('Country is required'); return; }
    if (!formData.currency)    { setError('Currency is required'); return; }

    // BILLING SAVES WITH THE PARTNER — checked before anything is written, saved after the partner. Taken from the
    // ref NOW: setLoading below swaps this form for "Loading partner…", which unmounts the tab. An untouched
    // Billing tab has nothing pending and never blocks the save.
    const billingTab = isEditMode ? billingRef.current : null;
    const billingPending = billingTab && billingTab.pending();
    if (billingPending && billingPending.problem) {
      setError(billingPending.problem);
      window.scrollTo({ top: 0, behavior: 'smooth' });   // the message is at the top; Update is at the bottom
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'create') {
        const res = await partnerService.createPartner(formData);
        navigate(`/partners/${res.data.insertId}?stripe_refresh=1`);
      } else if (isEditMode) {
        await partnerService.updatePartner(id, formData);
        if (billingPending) await billingTab.save();
        navigate(`/partners/${id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save partner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId, companyName) => {
    if (window.confirm(`Are you sure you want to delete hotel chain "${companyName}"?`)) {
      try {
        await accountService.deleteAccount(accountId);
        fetchAccounts(); // Refresh the list
      } catch (err) {
        alert('Failed to delete hotel chain: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  if (loading && (isViewMode || isEditMode)) {
    return <div className="loading">Loading partner...</div>;
  }

  return (
    <div className="partner-form-container">
      <div className="page-header">
        <h1>{mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} Partner</h1>
        <p className="page-subtitle">
          {mode === 'create' ? 'Add a new partner organization' :
           mode === 'edit' ? 'Update partner information' :
           'View partner details and associated hotel chains'}
        </p>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="partner-form" noValidate>
        <div className="form-row">
          <div className="form-group">
            <label>Company Name <span className="required">*</span></label>
            <input type="text" name="companyname" value={formData.companyname} onChange={handleChange} disabled={isViewMode} required />
          </div>
          <div className="form-group">
            <label>Email <span className="required">*</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isViewMode} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone</label>
            <input type="text" name="phone" value={formData.phone} onChange={handleChange} disabled={isViewMode} />
          </div>
          <div className="form-group">
            <label>Mobile</label>
            <input type="text" name="mobile" value={formData.mobile} onChange={handleChange} disabled={isViewMode} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Country <span className="required">*</span></label>
            <CountrySelect
              name="country"
              value={formData.country}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>
          <div className="form-group">
            <label>Currency <span className="required">*</span></label>
            <CurrencySelect name="currency" value={formData.currency} onChange={handleChange} disabled={isViewMode} placeholder="Select a currency..." />
          </div>
        </div>


        {id && (
          <div className="partner-tabs-section">
            <div className="partner-tabs">
              <button
                type="button"
                className={`tab-button ${activeTab === 'hotelchains' ? 'active' : ''}`}
                onClick={() => setActiveTab('hotelchains')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V18h14v-1.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                <span className="tab-label">Hotel Chains ({accounts.length})</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
                onClick={() => setActiveTab('billing')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Billing</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'sso' ? 'active' : ''}`}
                onClick={() => setActiveTab('sso')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="3" y="10" width="14" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 10V7a3 3 0 0 1 6 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">SSO</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'stripe' ? 'active' : ''}`}
                onClick={() => setActiveTab('stripe')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span className="tab-label">Stripe</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                <span className="tab-label">Users</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 8h8M6 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Products</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'revshare' ? 'active' : ''}`}
                onClick={() => setActiveTab('revshare')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 6v1m0 6v1M7.5 9.5a2.5 2.5 0 015 0c0 1.38-2.5 2-2.5 2s-2.5.62-2.5 2a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Rev Share</span>
              </button>
            </div>

            <div className="tab-content-area">
              {activeTab === 'hotelchains' && (
                <div className="tab-panel">
                  <div className="tab-panel-header">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by hotel name..."
                      value={accountSearch}
                      onChange={e => setAccountSearch(e.target.value)}
                    />
                    {(isSuperAdmin || formData.skip_stripe === 'Y' || formData.stripe_onboarding_complete) ? (
                      <Link to={`/partners/${id}/account/new`} className="btn-add">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Hotel Chain
                      </Link>
                    ) : (
                      <span className="btn-add btn-add-disabled" title="Connect Stripe to add hotel chains">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Hotel Chain
                      </span>
                    )}
                  </div>

                  {!isSuperAdmin && formData.skip_stripe !== 'Y' && !formData.stripe_onboarding_complete && (
                    <div className="stripe-required-notice">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M8 5v4M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Stripe must be connected before you can create hotel chains, hotels, or vaults.
                      <button type="button" className="btn-link" onClick={() => setActiveTab('stripe')}>Set up Stripe →</button>
                    </div>
                  )}

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Company Name</th>
                          <th>Country</th>
                          <th>Currency</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.length === 0 ? (
                          <tr><td colSpan="6" className="no-data">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                              <path d="M32 22c3.32 0 6-2.68 6-6s-2.68-6-6-6-6 2.68-6 6 2.68 6 6 6zM16 22c3.32 0 6-2.68 6-6s-2.68-6-6-6-6 2.68-6 6 2.68 6 6 6zm0 4c-4.66 0-14 2.34-14 7v3h28v-3c0-4.66-9.34-7-14-7z" fill="#d1d5db"/>
                            </svg>
                            <p>No hotel chains added yet</p>
                            <p className="no-data-hint">Add a hotel chain to get started</p>
                          </td></tr>
                        ) : (
                          (() => {
                            const filteredAccounts = accounts
                              .filter(acc => {
                                const name = acc.COMPANYNAME || acc.companyname || acc.name || '';
                                return name.toLowerCase().includes(accountSearch.toLowerCase());
                              })
                              .sort((a, b) => {
                                const nameA = (a.COMPANYNAME || a.companyname || a.name || '').toLowerCase();
                                const nameB = (b.COMPANYNAME || b.companyname || b.name || '').toLowerCase();
                                return nameA.localeCompare(nameB);
                              });
                            const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage);
                            const currentPage = Math.min(accountsCurrentPage, totalPages || 1);
                            const startIndex = (currentPage - 1) * accountsPerPage;
                            const endIndex = startIndex + accountsPerPage;

                            if (filteredAccounts.length === 0) {
                              return (
                                <tr><td colSpan="6" className="no-data">
                                  <p>No hotel chains match "{accountSearch}"</p>
                                </td></tr>
                              );
                            }

                            return filteredAccounts.slice(startIndex, endIndex).map(acc => {
                              const accountId = acc.ACCOUNTID || acc.accountid || acc.id;
                              const companyName = acc.COMPANYNAME || acc.companyname || acc.name;
                              const country = acc.COUNTRY || acc.country || '-';
                              const currency = acc.CURRENCY || acc.currency || '-';
                              const status = acc.ALLOWYN || acc.allowyn || acc.allowYn || 'N';

                              return (
                                <tr
                                  key={accountId}
                                  className="clickable-row"
                                  onClick={() => navigate(`/accounts/${accountId}`, { state: { partnerTab: activeTab } })}
                                  title="Click to view"
                                >
                                  <td><span className="id-badge">{accountId}</span></td>
                                  <td className="primary-text">{companyName}</td>
                                  <td>{country}</td>
                                  <td><span className="currency-badge">{currency}</span></td>
                                  <td>
                                    <span className={`status-badge status-${status === 'Y' ? 'active' : 'inactive'}`}>
                                      {status === 'Y' ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="actions-cell">
                                    <button
                                      className="btn-action btn-edit-action"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/accounts/${accountId}/edit`, { state: { partnerTab: activeTab } });
                                      }}
                                      title="Edit"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                      </svg>
                                    </button>
                                    <button
                                      className="btn-action btn-delete-action"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAccount(accountId, companyName);
                                      }}
                                      title="Delete"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              );
                            });
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>

                  {(() => {
                    const filteredCount = accounts.filter(acc => {
                      const name = acc.COMPANYNAME || acc.companyname || acc.name || '';
                      return name.toLowerCase().includes(accountSearch.toLowerCase());
                    }).length;
                    const totalPages = Math.ceil(filteredCount / accountsPerPage);
                    if (filteredCount <= accountsPerPage) return null;
                    return (
                      <div className="pagination">
                        <button
                          onClick={() => setAccountsCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={accountsCurrentPage === 1}
                        >
                          Previous
                        </button>
                        <span>
                          Page {Math.min(accountsCurrentPage, totalPages)} of {totalPages}
                        </span>
                        <button
                          onClick={() => setAccountsCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={accountsCurrentPage >= totalPages}
                        >
                          Next
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'sso' && (
                <div className="tab-panel">
                  <PartnerSsoSettings partnerId={id} />
                </div>
              )}

              {activeTab === 'stripe' && (
                <div className="tab-panel">
                  <StripeConnectPanel
                    partnerId={id}
                    returnPath={`/partners/${id}`}
                    canWaive={isSuperAdmin}
                    reloadKey={activeTab}
                  />
                </div>
              )}

              {/* Shared with partner settings, the hotel and chain Billing tabs. It replaced Addresses: V564
                  copied each partner's billing address onto the partner row it now edits. */}
              {/* Kept mounted while editing (hidden on the other tabs), so Update Partner can save it. */}
              {(activeTab === 'billing' || isEditMode) && (
                <div className="tab-panel" style={activeTab === 'billing' ? undefined : { display: 'none' }}>
                  <BillingTab ref={billingRef} entity="partner" entityId={id} mode={isEditMode ? 'edit' : 'view'} />
                </div>
              )}
              {activeTab === 'users' && (
                <div className="tab-panel">
                  <PartnerUsers partnerId={id} />
                </div>
              )}

              {activeTab === 'products' && (
                <div className="tab-panel">
                  <div className="tab-panel-header">
                    <p className="tab-panel-description">Set partner-specific override pricing for each product. These override the default pricing at checkout.</p>
                  </div>

                  <div className="form-section" style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Invoicing</h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: isViewMode ? 'default' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.invoices_enabled}
                        onChange={e => !isViewMode && setFormData(prev => ({ ...prev, invoices_enabled: e.target.checked ? 1 : 0 }))}
                        disabled={isViewMode}
                        style={{ width: '1rem', height: '1rem', cursor: isViewMode ? 'default' : 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#374151' }}>Enable invoice creation for this partner</span>
                    </label>
                    {!isViewMode && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <button type="button" className="btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                          onClick={async () => {
                            try {
                              await partnerService.updatePartner(id, { invoices_enabled: formData.invoices_enabled });
                              flashProductsMsg('Invoicing setting saved');
                            } catch (err) {
                              flashProductsMsg('Failed to save invoicing setting', 'error');
                            }
                          }}
                        >
                          Save Invoicing Setting
                        </button>
                      </div>
                    )}
                  </div>

                  {productsMsg && (
                    <div className={`products-msg products-msg-${productsMsg.type}`}>{productsMsg.text}</div>
                  )}

                  {productsLoading ? (
                    <div className="loading">Loading products...</div>
                  ) : platformProducts.length === 0 ? (
                    <div className="no-data-hint" style={{ padding: '1rem 0' }}>No products configured. Add products in Admin → Products.</div>
                  ) : (
                    <>
                      <div className="form-section" style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Products this partner sells</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {platformProducts.map(p => (
                            <label key={p.name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={selectedProducts.has(p.name)}
                                onChange={e => setSelectedProducts(prev => {
                                  const next = new Set(prev);
                                  e.target.checked ? next.add(p.name) : next.delete(p.name);
                                  return next;
                                })}
                                style={{ width: '1rem', height: '1rem' }}
                              />
                              <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>{p.display_name}</span>
                            </label>
                          ))}
                        </div>
                        <div style={{ marginTop: '0.75rem' }}>
                          <button
                            type="button"
                            className="btn-primary"
                            style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                            disabled={productsSaving}
                            onClick={async () => {
                              setProductsSaving(true);
                              try {
                                const productIds = platformProducts
                                  .filter(p => selectedProducts.has(p.name))
                                  .map(p => p.id);
                                await pricingService.savePartnerPlatformProducts(id, productIds);
                                flashProductsMsg('Products saved');
                              } catch (err) {
                                flashProductsMsg('Failed to save products', 'error');
                              } finally {
                                setProductsSaving(false);
                              }
                            }}
                          >
                            {productsSaving ? 'Saving...' : 'Save Products'}
                          </button>
                        </div>
                      </div>

                      {platformProducts
                        .filter(p => selectedProducts.has(p.name))
                        .map(renderProductOverrideSection)
                      }
                    </>
                  )}
                </div>
              )}

              {activeTab === 'revshare' && (
                <div className="tab-panel">
                  <div className="tab-panel-header">
                    <p className="tab-panel-description">
                      Revenue share tiers for this partner. If no partner-specific tiers are set, the default tiers apply.
                    </p>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleCopyDefault}
                      disabled={revShareSaving}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                    >
                      Copy from Default
                    </button>
                  </div>

                  <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1rem' }}>
                    {REV_SHARE_PRODUCTS.map(p => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setRevShareProduct(p.key)}
                        style={{
                          padding: '6px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.8125rem',
                          fontWeight: revShareProduct === p.key ? 700 : 400,
                          color: revShareProduct === p.key ? '#1e40af' : '#64748b',
                          borderBottom: revShareProduct === p.key ? '2px solid #1e40af' : '2px solid transparent',
                          marginBottom: '-2px',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {revShareMsg && (
                    <div style={{
                      padding: '0.6rem 0.875rem',
                      background: revShareMsg.type === 'error' ? '#fee2e2' : '#dcfce7',
                      color: revShareMsg.type === 'error' ? '#dc2626' : '#166534',
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      fontSize: '0.8125rem'
                    }}>
                      {revShareMsg.text}
                    </div>
                  )}

                  {revShareLoading ? (
                    <div className="loading">Loading...</div>
                  ) : (
                    <RevShareTierTable
                      tiers={revShareTiers}
                      onSave={handleRevShareSave}
                      onDelete={handleRevShareDelete}
                      onAdd={handleRevShareAdd}
                      saving={revShareSaving}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-actions">
          {!isViewMode && <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : mode === 'create' ? 'Create Partner' : 'Update Partner'}</button>}
          {isViewMode && <button type="button" className="btn-secondary" onClick={() => navigate(`/partners/${id}/edit`)}>Edit Partner</button>}
          <button type="button" className="btn-secondary" onClick={() => navigate('/partners')}>{isViewMode ? 'Back' : 'Cancel'}</button>
        </div>
      </form>

      {partnerPriceModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add Price</h3>
            <div className="form-row-modal">
              <div className="form-group">
                <label>Amount <span className="required">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={partnerPriceModal.amount}
                  onChange={e => setPartnerPriceModal(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Currency <span className="required">*</span></label>
                <CurrencySelect billing value={partnerPriceModal.currency} onChange={e => setPartnerPriceModal(p => ({ ...p, currency: e.target.value }))} />
              </div>
            </div>
            {partnerPriceModal.lookup_key && (
              <div className="form-group">
                <label>Lookup Key</label>
                <input type="text" value={partnerPriceModal.lookup_key} readOnly className="input-readonly" />
              </div>
            )}
            <div className="form-group">
              <label>Nickname <span className="optional">(optional)</span></label>
              <input
                type="text"
                value={partnerPriceModal.nickname}
                onChange={e => setPartnerPriceModal(p => ({ ...p, nickname: e.target.value }))}
                placeholder="e.g. Partner annual plan"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setPartnerPriceModal(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                onClick={savePartnerPrice}
                disabled={productsSaving || !partnerPriceModal.amount || parseFloat(partnerPriceModal.amount) <= 0}
              >
                {productsSaving ? 'Creating...' : 'Add Price'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PartnerForm;
